import { Logo } from '@/shared/components/ui/Logo';
import { ApiError } from '@/shared/lib/api-client';
import { useAuthStore } from '@/shared/stores/auth-store';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertTriangle, ArrowLeft, ArrowRight, CheckCircle2, Eye, EyeOff, Lock, Mail, Search, Server, ShieldCheck } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  loginRequest,
  superSupportEnterRequest,
  superSupportTotpRequest,
  supportLoginTotpRequest,
  supportSelectTenantRequest,
  type UnavailableServer,
  type SupportTotpResult,
} from './api';

const loginFormSchema = z.object({
  email: z.string().email('Email inválido'),
  password: z.string().min(1, 'Senha é obrigatória'),
});

type LoginFormValues = z.infer<typeof loginFormSchema>;

type Stage = 'credentials' | 'support_totp' | 'support_select_tenant';

export interface LoginFormProps {
  onSuccess: () => void;
}

/**
 * Empresa na lista de escolha. `server_id`/`server_name` só vêm da homologação
 * central — no caminho antigo só existe um servidor, e dizer o nome dele em toda
 * linha seria ruído.
 */
type TenantOption = {
  id: number;
  name: string;
  slug: string;
  status: string;
  server_id?: number;
  server_name?: string;
};

/** O que a etapa 3 precisa, independente do caminho que trouxe até aqui. */
interface TenantChoice {
  /** Só no caminho antigo — a homologação central usa o homologatedToken. */
  supportPendingToken: string;
  supportEmail: string;
  tenants: TenantOption[];
}

export function LoginForm({ onSuccess }: LoginFormProps) {
  const [stage, setStage] = useState<Stage>('credentials');
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [challengeToken, setChallengeToken] = useState('');
  const [supportData, setSupportData] = useState<TenantChoice | null>(null);
  const [totpCode, setTotpCode] = useState('');
  /** Qual caminho o SERVIDOR escolheu na etapa 1. A tela não decide — carrega. */
  const [via, setVia] = useState<'super' | 'local'>('local');
  /** O caminho super não devolve o email (quem sabe é o painel); guardamos o
   *  digitado, que é o mesmo, pra mostrar no cabeçalho da escolha. */
  const [supportEmail, setSupportEmail] = useState('');
  const [homologatedToken, setHomologatedToken] = useState('');
  const [unavailableServers, setUnavailableServers] = useState<UnavailableServer[]>([]);
  /** "Não pedir o código neste computador por 24h" — marcado por padrão, igual ao MetricsFarma. */
  const [lembrarComputador, setLembrarComputador] = useState(true);
  const setSession = useAuthStore((s) => s.setSession);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: { email: '', password: '' },
  });

  const handleStage1 = handleSubmit(async (values) => {
    setServerError(null);
    setSubmitting(true);
    try {
      const outcome = await loginRequest(values);
      if (outcome.kind === 'support_homologated') {
        // Computador confiável: a senha bastou, o código foi dispensado.
        setVia('super');
        setSupportEmail(values.email);
        setHomologatedToken(outcome.result.homologatedToken);
        setUnavailableServers(outcome.result.unavailableServers);
        setSupportData({
          supportPendingToken: '',
          supportEmail: values.email,
          tenants: outcome.result.tenants,
        });
        setStage('support_select_tenant');
        return;
      }
      if (outcome.kind === 'support_totp_required') {
        setChallengeToken(outcome.challengeToken);
        setVia(outcome.via);
        setSupportEmail(values.email);
        setStage('support_totp');
        return;
      }
      setSession(outcome.result.accessToken, outcome.result.user);
      onSuccess();
    } catch (err) {
      if (err instanceof ApiError) {
        // Pra USER_INACTIVE / TENANT_SUSPENDED / outros 403, mostra title + detail
        // pra usuário entender O QUE aconteceu. AUTH_INVALID_CREDENTIALS continua
        // genérico pra não vazar info de "conta existe".
        const msg =
          err.code === 'AUTH_INVALID_CREDENTIALS'
            ? 'Email ou senha incorretos'
            : err.problem.detail
              ? `${err.problem.title} — ${err.problem.detail}`
              : err.problem.title;
        setServerError(msg);
      } else {
        setServerError('Não foi possível conectar. Tente novamente.');
      }
    } finally {
      setSubmitting(false);
    }
  });

  const handleSupportTotp = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (totpCode.length !== 6) return;
    setServerError(null);
    setSubmitting(true);
    try {
      if (via === 'super') {
        const r = await superSupportTotpRequest(challengeToken, totpCode, lembrarComputador);
        setHomologatedToken(r.homologatedToken);
        setUnavailableServers(r.unavailableServers);
        // Mesmo formato do caminho antigo — a tela de escolha não sabe a diferença,
        // só recebe empresas que agora carregam o servidor de cada uma.
        setSupportData({ supportPendingToken: '', supportEmail, tenants: r.tenants });
      } else {
        const result = await supportLoginTotpRequest(challengeToken, totpCode);
        setSupportData(result);
      }
      setStage('support_select_tenant');
      setTotpCode('');
    } catch (err) {
      if (err instanceof ApiError) {
        setServerError(err.problem.detail ?? err.problem.title);
      } else {
        setServerError('Falha ao validar código. Tente novamente.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSelectTenant = async (tenantId: number, serverId?: number): Promise<void> => {
    if (!supportData) return;
    setServerError(null);
    setSubmitting(true);
    try {
      const result =
        via === 'super' && serverId !== undefined
          ? await superSupportEnterRequest(homologatedToken, serverId, tenantId)
          : await supportSelectTenantRequest(supportData.supportPendingToken, tenantId);
      setSession(result.accessToken, result.user);
      onSuccess();
    } catch (err) {
      if (err instanceof ApiError) {
        setServerError(err.problem.detail ?? err.problem.title);
      } else {
        setServerError('Falha ao entrar no tenant.');
      }
      setSubmitting(false);
    }
  };

  const goBackToCredentials = (): void => {
    setStage('credentials');
    setChallengeToken('');
    setTotpCode('');
    setSupportData(null);
    setServerError(null);
  };

  return (
    <div className="force-light flex h-screen w-screen overflow-hidden bg-n-900">
      {/* ─── Left side: art / pitch ─── */}
      <div className="relative hidden flex-1 flex-col justify-center px-20 py-20 text-white lg:flex">
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background: 'linear-gradient(135deg, var(--brand-700) 0%, var(--n-900) 100%)',
          }}
          aria-hidden="true"
        />
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'radial-gradient(circle at 20% 30%, rgba(10,78,227,0.5) 0%, transparent 50%)',
          }}
          aria-hidden="true"
        />

        <div className="relative mb-12">
          <Logo tone="dark" height={48} />
        </div>

        <h1 className="relative mb-6 max-w-[640px] text-[56px] font-extrabold leading-[1.1] tracking-[-0.025em]">
          Atendimento omnichannel,
          <br />
          simples como deveria ser.
        </h1>
        <p className="relative max-w-[520px] text-[18px] leading-relaxed text-white/70">
          Centralize WhatsApp, WABA, chatbots e campanhas em um só lugar. Acompanhe seu time em
          tempo real.
        </p>

        <div className="absolute bottom-8 left-20 right-20 flex items-center justify-between text-xs text-white/40">
          <span>© {new Date().getFullYear()} ChatPlus · Todos os direitos reservados</span>
          <span>v1.0.0</span>
        </div>
      </div>

      {/* ─── Right side: form ─── */}
      <div className="flex w-full flex-col justify-center bg-n-900 px-8 py-12 text-white lg:w-[520px] lg:px-16 lg:py-16">
        <div className="mb-9 flex items-center">
          <Logo tone="dark" height={36} />
        </div>

        {stage === 'credentials' && (
          <CredentialsStage
            onSubmit={handleStage1}
            register={register}
            errors={errors}
            submitting={submitting}
            showPassword={showPassword}
            toggleShowPassword={() => setShowPassword((v) => !v)}
            serverError={serverError}
          />
        )}

        {stage === 'support_totp' && (
          <SupportTotpStage
            code={totpCode}
            onCodeChange={setTotpCode}
            onSubmit={handleSupportTotp}
            onBack={goBackToCredentials}
            submitting={submitting}
            serverError={serverError}
            // Só no caminho central: quem guarda a confiança é o painel. O caminho
            // local (fallback, painel fora) segue pedindo o código sempre.
            lembrar={via === 'super' ? lembrarComputador : undefined}
            onLembrarChange={setLembrarComputador}
          />
        )}

        {stage === 'support_select_tenant' && supportData && (
          <SelectTenantStage
            supportEmail={supportData.supportEmail}
            tenants={supportData.tenants}
            unavailableServers={unavailableServers}
            onSelect={handleSelectTenant}
            onBack={goBackToCredentials}
            submitting={submitting}
            serverError={serverError}
          />
        )}
      </div>
    </div>
  );
}

/* ─── Stage 1: credentials ──────────────────────────────────────── */

interface CredentialsStageProps {
  onSubmit: (e: React.FormEvent) => void;
  register: ReturnType<typeof useForm<LoginFormValues>>['register'];
  errors: ReturnType<typeof useForm<LoginFormValues>>['formState']['errors'];
  submitting: boolean;
  showPassword: boolean;
  toggleShowPassword: () => void;
  serverError: string | null;
}

function CredentialsStage(props: CredentialsStageProps) {
  return (
    <>
      <div className="mb-1.5 text-[28px] font-bold text-white">Bem vindo!</div>
      <div className="mb-8 text-sm text-white/60">Faça login pra começar a atender.</div>

      <form onSubmit={props.onSubmit} className="space-y-3.5">
        <div>
          <label
            htmlFor="email"
            className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.06em] text-white/50"
          >
            E-mail
          </label>
          <div
            className={`flex items-center gap-2.5 rounded-md border bg-white/[0.06] px-3.5 py-3 transition focus-within:border-brand-400 focus-within:shadow-[0_0_0_3px_rgba(10,78,227,0.25)] ${
              props.errors.email ? 'border-danger-500/60' : 'border-white/[0.14]'
            }`}
          >
            <Mail size={16} className="shrink-0 text-white/50" aria-hidden="true" />
            <input
              id="email"
              type="email"
              autoComplete="email"
              placeholder="meu@email.com"
              {...props.register('email')}
              className="flex-1 border-none bg-transparent text-sm text-white outline-none placeholder:text-white/40"
            />
          </div>
          {props.errors.email && (
            <p className="mt-1.5 text-xs text-danger-500">{props.errors.email.message}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="password"
            className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.06em] text-white/50"
          >
            Senha
          </label>
          <div
            className={`flex items-center gap-2.5 rounded-md border bg-white/[0.06] px-3.5 py-3 transition focus-within:border-brand-400 focus-within:shadow-[0_0_0_3px_rgba(10,78,227,0.25)] ${
              props.errors.password ? 'border-danger-500/60' : 'border-white/[0.14]'
            }`}
          >
            <Lock size={16} className="shrink-0 text-white/50" aria-hidden="true" />
            <input
              id="password"
              type={props.showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="•••••••••"
              {...props.register('password')}
              className="flex-1 border-none bg-transparent text-sm text-white outline-none placeholder:text-white/40"
            />
            <button
              type="button"
              onClick={props.toggleShowPassword}
              aria-label={props.showPassword ? 'Ocultar senha' : 'Mostrar senha'}
              className="text-white/50 hover:text-white/80"
            >
              {props.showPassword ? (
                <Eye size={16} aria-hidden="true" />
              ) : (
                <EyeOff size={16} aria-hidden="true" />
              )}
            </button>
          </div>
          {props.errors.password && (
            <p className="mt-1.5 text-xs text-danger-500">{props.errors.password.message}</p>
          )}
        </div>

        {props.serverError && (
          <div
            role="alert"
            className="rounded-md border border-danger-500/30 bg-danger-500/10 px-3 py-2 text-sm text-danger-500"
          >
            {props.serverError}
          </div>
        )}

        <div className="flex items-center justify-between pt-7">
          <button
            type="button"
            className="text-[13px] text-brand-300 hover:text-brand-200"
            onClick={() => {
              /* TODO: rota de recuperação de senha (Epic 7+) */
            }}
          >
            Recuperar minha senha
          </button>
          <button
            type="submit"
            disabled={props.submitting}
            className="inline-flex items-center gap-2 rounded-md bg-brand-500 px-7 py-[13px] text-[13px] font-bold uppercase tracking-[0.05em] text-white shadow-[0_8px_24px_-8px_rgba(10,78,227,0.5)] transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {props.submitting ? 'ENTRANDO...' : 'LOGIN'}
            {!props.submitting && <ArrowRight size={14} aria-hidden="true" />}
          </button>
        </div>
      </form>
    </>
  );
}

/* ─── Stage 2: TOTP do support login ────────────────────────────── */

interface SupportTotpStageProps {
  code: string;
  onCodeChange: (code: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onBack: () => void;
  submitting: boolean;
  serverError: string | null;
  /** undefined = opção não se aplica (caminho local) e o checkbox não aparece. */
  lembrar?: boolean | undefined;
  onLembrarChange: (lembrar: boolean) => void;
}

function SupportTotpStage(props: SupportTotpStageProps) {
  return (
    <>
      <div className="mb-4 inline-flex items-center gap-2 rounded-pill bg-warning-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-warning-500 self-start">
        <ShieldCheck size={12} />
        Modo Suporte
      </div>
      <div className="mb-1.5 text-[28px] font-bold text-white">Verificação em duas etapas</div>
      <div className="mb-8 text-sm text-white/60">
        Abra seu app autenticador (Google Authenticator, Authy, 1Password) e digite o código de 6
        dígitos.
      </div>

      <form onSubmit={props.onSubmit} className="space-y-4">
        <div>
          <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.06em] text-white/50">
            Código TOTP
          </label>
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            pattern="\d{6}"
            autoComplete="one-time-code"
            required
            value={props.code}
            onChange={(e) => props.onCodeChange(e.target.value.replace(/\D/g, ''))}
            autoFocus
            className="w-full rounded-md border border-white/[0.14] bg-white/[0.06] px-4 py-4 text-center text-[28px] font-bold tracking-[0.5em] text-white outline-none focus:border-brand-400 focus:shadow-[0_0_0_3px_rgba(10,78,227,0.25)] placeholder:text-white/30"
            placeholder="••••••"
          />
        </div>

        {props.lembrar !== undefined && (
          <label className="flex cursor-pointer items-center gap-2.5 text-[13px] text-white/70">
            <input
              type="checkbox"
              checked={props.lembrar}
              onChange={(e) => props.onLembrarChange(e.target.checked)}
              className="h-[16px] w-[16px] cursor-pointer accent-brand-500"
            />
            Não pedir o código neste computador por 24h
          </label>
        )}

        {props.serverError && (
          <div
            role="alert"
            className="rounded-md border border-danger-500/30 bg-danger-500/10 px-3 py-2 text-sm text-danger-500"
          >
            {props.serverError}
          </div>
        )}

        <div className="flex items-center justify-between pt-3">
          <button
            type="button"
            onClick={props.onBack}
            className="inline-flex items-center gap-1.5 text-[13px] text-white/60 hover:text-white"
          >
            <ArrowLeft size={14} />
            Voltar
          </button>
          <button
            type="submit"
            disabled={props.submitting || props.code.length !== 6}
            className="inline-flex items-center gap-2 rounded-md bg-brand-500 px-7 py-[13px] text-[13px] font-bold uppercase tracking-[0.05em] text-white shadow-[0_8px_24px_-8px_rgba(10,78,227,0.5)] transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {props.submitting ? 'VALIDANDO...' : 'VERIFICAR'}
            {!props.submitting && <ArrowRight size={14} aria-hidden="true" />}
          </button>
        </div>
      </form>
    </>
  );
}

/* ─── Stage 3: escolher tenant ──────────────────────────────────── */

interface Tenant {
  id: number;
  name: string;
  slug: string;
  status: 'active' | 'suspended' | 'trial';
}

interface SelectTenantStageProps {
  supportEmail: string;
  tenants: TenantOption[];
  /** `serverId` presente = veio da homologação central (empresa pode ser de outro servidor). */
  onSelect: (tenantId: number, serverId?: number) => Promise<void>;
  onBack: () => void;
  submitting: boolean;
  serverError: string | null;
  /** Servidores que não responderam. Aparecem na TELA de propósito: sem isso, a
   *  pessoa procuraria a empresa, não acharia, e concluiria que ela sumiu. */
  unavailableServers?: UnavailableServer[];
}



function SelectTenantStage(props: SelectTenantStageProps) {
  const [filter, setFilter] = useState('');

  const filtered = useMemo(() => {
    const f = filter.toLowerCase().trim();
    if (!f) return props.tenants;
    return props.tenants.filter(
      (t) => t.name.toLowerCase().includes(f) || t.slug.toLowerCase().includes(f),
    );
  }, [filter, props.tenants]);

  return (
    <>
      <div className="mb-4 inline-flex items-center gap-2 rounded-pill bg-warning-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-warning-500 self-start">
        <ShieldCheck size={12} />
        Modo Suporte
      </div>
      <div className="mb-1.5 text-[28px] font-bold text-white">Escolher Cliente</div>
      <div className="mb-6 text-sm text-white/60">
        Logado como <span className="font-mono text-brand-300">{props.supportEmail}</span>. Em qual
        instância entrar?
      </div>

      <div className="mb-3 flex items-center gap-2 rounded-md border border-white/[0.14] bg-white/[0.06] px-3 py-2">
        <Search size={14} className="shrink-0 text-white/50" />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Buscar por nome ou slug..."
          className="flex-1 border-none bg-transparent text-sm text-white outline-none placeholder:text-white/40"
        />
      </div>

      {props.unavailableServers && props.unavailableServers.length > 0 && (
        <div
          role="status"
          className="mb-3 rounded-md border border-warning-500/30 bg-warning-500/10 px-3 py-2 text-[12px] text-warning-500"
        >
          <div className="flex items-center gap-1.5 font-semibold">
            <AlertTriangle size={12} />
            {props.unavailableServers.length === 1
              ? '1 servidor não respondeu'
              : `${props.unavailableServers.length} servidores não responderam`}
          </div>
          <div className="mt-0.5 text-warning-500/80">
            As empresas de {props.unavailableServers.map((s) => s.server_name).join(', ')} não estão
            nesta lista. Elas não sumiram — o servidor é que não respondeu agora.
          </div>
        </div>
      )}

      <div className="max-h-[360px] overflow-auto space-y-2 pr-1">
        {filtered.length === 0 && (
          <div className="py-8 text-center text-[12px] text-white/40">
            Nenhuma instância encontrada.
          </div>
        )}
        {filtered.map((t) => {
          const suspended = t.status === 'suspended';
          return (
            <button
              // A chave PRECISA incluir o servidor: os ids colidem entre eles
              // (cada banco tem sua sequência), então existe "empresa 5" em mais
              // de um lugar e o React embaralharia as duas.
              key={t.server_id ? `${t.server_id}:${t.id}` : t.id}
              type="button"
              disabled={props.submitting}
              onClick={() => void props.onSelect(t.id, t.server_id)}
              className="w-full flex items-center justify-between gap-3 rounded-md border border-white/[0.14] bg-white/[0.04] px-4 py-3 text-left transition hover:border-brand-400 hover:bg-brand-500/10 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <div className="min-w-0 flex-1">
                <div className="text-[14px] font-semibold text-white truncate">{t.name}</div>
                <div className="flex items-center gap-1.5 text-[11px] text-white/50 truncate">
                  <span className="font-mono">{t.slug}</span>
                  {/* Só na homologação central: com um servidor só, dizer o nome
                      dele em toda linha é ruído. */}
                  {t.server_name && (
                    <>
                      <span className="text-white/25">·</span>
                      <span className="inline-flex items-center gap-1 text-white/40">
                        <Server size={10} />
                        {t.server_name}
                      </span>
                    </>
                  )}
                </div>
              </div>
              {suspended ? (
                <span className="rounded-pill bg-danger-500/20 px-2 py-0.5 text-[10px] font-semibold text-danger-300">
                  SUSPENSO
                </span>
              ) : (
                <span className="rounded-pill bg-success-500/20 px-2 py-0.5 text-[10px] font-semibold text-success-300">
                  ATIVO
                </span>
              )}
              <CheckCircle2 size={16} className="text-white/30 flex-shrink-0" />
            </button>
          );
        })}
      </div>

      {props.serverError && (
        <div
          role="alert"
          className="mt-3 rounded-md border border-danger-500/30 bg-danger-500/10 px-3 py-2 text-sm text-danger-500"
        >
          {props.serverError}
        </div>
      )}

      <div className="flex items-center justify-between pt-5">
        <button
          type="button"
          onClick={props.onBack}
          className="inline-flex items-center gap-1.5 text-[13px] text-white/60 hover:text-white"
        >
          <ArrowLeft size={14} />
          Voltar
        </button>
      </div>
    </>
  );
}
